import React, { useState } from 'react';
import { NavLink, Outlet, Navigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  FileSpreadsheet,
  Settings2,
  LogOut,
  Menu,
  X
} from 'lucide-react';
import Navbar from './Navbar';
import { canAccessSetup, normalizeStaffUser } from '../utils/roles';

const ALL_NAV = [
  { name: 'Dashboard', href: '/adminportal/dashboard', icon: LayoutDashboard, roles: ['super_admin', 'admin'] },
  { name: 'Members', href: '/adminportal/members', icon: Users, roles: ['super_admin', 'admin'] },
  { name: 'Reports', href: '/adminportal/reports', icon: FileSpreadsheet, roles: ['super_admin', 'admin'] },
  { name: 'Set up', href: '/adminportal/setup', icon: Settings2, roles: ['super_admin'] }
];

const roleLabel = (role) => {
  if (role === 'super_admin') return 'Super Admin';
  if (role === 'admin') return 'Admin';
  return 'User';
};

const AdminLayout = ({ user, onLogout }) => {
  if (!user) {
    return <Navigate to="/" replace />;
  }

  const normalized = normalizeStaffUser(user);
  const role = normalized.role || 'admin';
  const [menuOpen, setMenuOpen] = useState(false);
  const navItems = ALL_NAV.filter((item) => {
    if (item.name === 'Set up') return canAccessSetup(normalized);
    return item.roles.includes(role);
  });

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="min-h-screen bg-violet-50/60 flex">
      {menuOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-slate-900/40 lg:hidden"
          aria-label="Close menu"
          onClick={closeMenu}
        />
      )}
      <aside className={`w-[232px] shrink-0 bg-gradient-to-b from-primary-100 via-primary-50 to-violet-50 text-slate-800 flex flex-col fixed inset-y-0 left-0 z-50 border-r border-primary-200/80 transform transition-transform duration-200 lg:static lg:z-auto lg:translate-x-0 lg:h-screen lg:sticky lg:top-0 ${menuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="px-4 py-5 border-b border-primary-200/70">
          <div className="text-[10px] uppercase tracking-[0.18em] text-primary-600 font-semibold">Paytiemp</div>
          <h1 className="text-base font-semibold leading-snug mt-1 text-slate-900">PF Claims Management</h1>
        </div>

        <nav className="flex-1 px-2 py-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={closeMenu}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition ${
                    isActive
                      ? 'bg-primary-600 text-white shadow-sm'
                      : 'text-slate-700 hover:bg-primary-100/80 hover:text-slate-900'
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                <span>{item.name}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="p-3 border-t border-primary-200/70 space-y-2">
          <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-slate-700">
            <div className="h-8 w-8 rounded-full bg-primary-200 text-primary-800 flex items-center justify-center text-xs font-semibold">
              {(normalized.username || 'A').slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="truncate font-medium text-slate-900">{normalized.username}</div>
              <div className="text-[11px] text-slate-500">{roleLabel(role)}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={onLogout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm bg-white/70 hover:bg-white border border-primary-200 text-slate-700"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 shrink-0 bg-white border-b border-primary-100 px-3 sm:px-4 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-lg border border-primary-100 text-slate-700"
              onClick={() => setMenuOpen((open) => !open)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            >
              {menuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate">PF Claims Management</div>
              <div className="text-xs text-slate-500 truncate">Admin workspace · {roleLabel(role)}</div>
            </div>
          </div>
          <Navbar user={normalized} onLogout={onLogout} sidebarMode />
        </header>
        <main className="flex-1 p-4 sm:p-5 overflow-auto">
          <Outlet context={{ user: normalized }} />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
